import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { localDateSchema, moneySchema, quantitySchema, timestampSchema, uuidSchema, } from "../common/primitives.js";
import { fleetBillingRunStatusSchema } from "./enums.js";
import { fleetUsageSourceDocTypeSchema } from "./usage.js";
/**
 * Filo DÖNEM FATURASI (ADR 0017 §16) = fişli satış faturası (ADR 0010, `basis = FISCAL_RECEIPTS`): dönemdeki
 * faturalanmamış kullanım satırlarından ÖKC fiş bağı olanlar fişe göre toplanır; kesinleştirmede fatura GELİR / KDV /
 * açık kalem ÜRETMEZ (gün sonu yazdı). Fiş bağı olmayan satırlar (elle filo fişi) faturaya girmez → "istisna" (#65).
 * Karşı kayıtlarla tamamen iade edilmiş fişler (net ≤ 0) "iptal edilmiş" listesindedir. Başka bir faturaya (elle)
 * bağlanmış fişler "zaten faturalı" listesindedir ve bu dönem faturasına girmez.
 */
export const fleetBillingPeriodRequestSchema = z
    .object({
    companyId: uuidSchema,
    counterpartyId: uuidSchema,
    periodFrom: localDateSchema.optional().describe("Varsayılan: carinin fatura döneminde son biten dönem"),
    periodTo: localDateSchema.optional(),
})
    .refine((value) => (value.periodFrom === undefined) === (value.periodTo === undefined), {
    path: ["periodTo"],
    message: "Dönem başı ve sonu birlikte verilmelidir",
})
    .refine((value) => !value.periodFrom || !value.periodTo || value.periodFrom <= value.periodTo, {
    path: ["periodTo"],
    message: "Dönem sonu dönem başından önce olamaz",
});
export const fleetBillingReceiptSchema = z.object({
    fiscalDeviceSerial: z.string(),
    zNo: z.number().int(),
    receiptNo: z.number().int(),
    receiptDate: localDateSchema,
    stationId: uuidSchema.nullable(),
    amount: moneySchema.describe("Filoya yazılan tutar (karma ödemede filo payı; iade karşı kayıtları düşülmüş)"),
    litres: quantitySchema,
    entryCount: z.number().int(),
});
export const fleetBillingExceptionSchema = z.object({
    usageEntryId: uuidSchema,
    stationId: uuidSchema,
    occurredAt: timestampSchema,
    localDay: localDateSchema,
    sourceDocType: fleetUsageSourceDocTypeSchema,
    sourceDocId: uuidSchema,
    amount: moneySchema,
    quantity: quantitySchema,
    reason: z.enum(["NO_FISCAL_RECEIPT"]),
});
export const fleetBillingTotalsSchema = z.object({
    amount: moneySchema.describe("Faturaya girecek tutar = fiş tutarları toplamı"),
    litres: quantitySchema,
    receiptCount: z.number().int(),
    entryCount: z.number().int(),
    exceptionCount: z.number().int(),
    exceptionAmount: moneySchema,
    voidedCount: z.number().int(),
    alreadyInvoicedCount: z.number().int(),
});
export const fleetBillingPreviewSchema = z.object({
    companyId: uuidSchema,
    counterpartyId: uuidSchema,
    periodFrom: localDateSchema,
    periodTo: localDateSchema,
    receipts: z.array(fleetBillingReceiptSchema),
    exceptions: z.array(fleetBillingExceptionSchema),
    voided: z.array(fleetBillingReceiptSchema),
    alreadyInvoiced: z.array(fleetBillingReceiptSchema),
    totals: fleetBillingTotalsSchema,
    activeRunId: uuidSchema.nullable().describe("Bu dönem için etkin (taslak / kesilmiş) dönem faturası"),
});
export const createFleetBillingRunSchema = fleetBillingPeriodRequestSchema;
export const fleetBillingRunSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    counterpartyId: uuidSchema,
    counterpartyName: z.string(),
    periodFrom: localDateSchema,
    periodTo: localDateSchema,
    status: fleetBillingRunStatusSchema,
    invoiceId: uuidSchema.nullable(),
    invoiceNo: z.string().nullable(),
    invoiceStatus: z.string().nullable(),
    totalAmount: moneySchema,
    totalLitres: quantitySchema,
    entryCount: z.number().int(),
    receiptCount: z.number().int(),
    exceptionCount: z.number().int(),
    issuedAt: timestampSchema.nullable(),
    cancelledAt: timestampSchema.nullable(),
    cancelReason: z.string().nullable(),
    createdAt: timestampSchema,
    version: z.number().int(),
    receipts: z.array(fleetBillingReceiptSchema).describe("Liste ucunda boş; ayrıntıda dolu"),
});
export const fleetBillingRunPageSchema = pageOf(fleetBillingRunSchema);
export const listFleetBillingRunsQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema.optional(),
    counterpartyId: uuidSchema.optional(),
    status: fleetBillingRunStatusSchema.optional(),
});
export const issueFleetBillingRunSchema = z.object({
    version: z.number().int().positive(),
    issueDate: localDateSchema
        .optional()
        .describe("Varsayılan: bugün (İstanbul); en geç fişin tarihinden önce olamaz"),
    dueDate: localDateSchema.nullable().optional().describe("Boşsa fatura ayarındaki varsayılan vade"),
    notes: z.string().trim().max(1500).nullable().optional(),
});
export const cancelFleetBillingRunSchema = z.object({
    version: z.number().int().positive(),
    reason: z.string().trim().min(3).max(500),
});
//# sourceMappingURL=billing.js.map